import { useEffect, useState } from 'react';

import styles from './matrix.module.css';

export const IMG = '/assets/images/trial/matrix';
export const VIDEO = '/assets/video/trial/matrix';
export const FORM_ID = 'np-form';

/** Maps space-separated design class names to the CSS module's scoped names. */
export function m(names: string): string {
  return names
    .split(' ')
    .filter(Boolean)
    .map((name) => styles[name] ?? name)
    .join(' ');
}

/** One module class, for the imperative engines that toggle state classes on DOM they own. */
export function cls(name: string): string {
  return styles[name] ?? name;
}

/** Joins class strings, skipping falsy entries. */
export function cx(...names: Array<string | false | null | undefined>): string {
  return names.filter(Boolean).join(' ');
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches;
}

/** Follows the live system setting, like the design's `RM.addEventListener('change', …)`. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(REDUCED_MOTION);
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

/** The repo's locale codes → BCP 47, for Intl formatting (numbers, country names, lists). */
const INTL_LOCALES: Record<string, string> = { pt: 'pt-BR', en: 'en-US', es: 'es', fr: 'fr', ru: 'ru', tr: 'tr', sa: 'ar-u-nu-latn', cn: 'zh-CN', jp: 'ja', kr: 'ko' };

export function intlLocale(locale: string | undefined): string {
  return INTL_LOCALES[locale ?? 'en'] ?? 'en-US';
}

export function formatNumber(value: number, locale: string | undefined): string {
  try {
    return new Intl.NumberFormat(intlLocale(locale)).format(value);
  } catch {
    return String(value);
  }
}
